import React from "react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import cuentaMask from "../../../../Components/cuentaMask";
import dineroDisabledMask from "../../../../Components/dineroDisabledMask";

/*
  Grafica del Estado de Resultados por Periodo.

  Una sola definicion alimenta los tres destinos: la pantalla la dibuja nivo, y
  el PDF y el Excel reciben ESE mismo SVG rasterizado a PNG (`rasterizarGrafica`).
  Asi no pueden decir cosas distintas, que es la misma regla que sigue
  `ExcelTablaReporte` con la tabla.

  El PNG no es un capricho: pdfmake 0.1.x no dibuja SVG (eso llego en 0.2) y
  `xlsx-js-style` no sabe escribir graficas nativas de Excel. En el Excel la
  grafica es una imagen, no un objeto grafico editable.
*/

// Paleta categorica validada con el script de la guia de visualizacion contra la
// superficie blanca de la app:
//   node scripts/validate_palette.js "<hex,...>" --mode light --surface "#FFFFFF"
//   -> lightness band, chroma floor, separacion CVD y piso de vision normal PASS.
// El ORDEN es el mecanismo de seguridad para daltonismo, no decoracion: no
// reordenar ni meter colores nuevos sin volver a correr el validador.
//
// El validador marca WARN de contraste en aqua, amarillo y magenta (<3:1 sobre
// blanco). La regla exige "relief": etiquetas visibles o vista de tabla. Aqui la
// tabla del reporte va justo debajo con los mismos numeros, mas la leyenda y los
// tooltips, asi que el color nunca carga solo la identidad de una serie.
export const PALETA_SERIES = [
  "#2a78d6", // azul
  "#eb6834", // naranja
  "#1baf7a", // aqua
  "#eda100", // amarillo
  "#e87ba4", // magenta
  "#008300", // verde
  "#4a3aa7", // violeta
  "#e34948", // rojo
];

// Nunca se cicla la paleta: una novena serie no recibe un color inventado, se
// queda fuera y la UI lo dice.
export const MAX_SERIES = PALETA_SERIES.length;

// Hasta aqui, barras agrupadas (comparan mejor pocos periodos); de ahi en
// adelante, lineas (leen mejor la tendencia y no se saturan con 52 semanas).
export const MAX_PERIODOS_BARRAS = 6;

const TINTA_SUAVE = "#52514e";
const MUTED = "#898781";
const REJILLA = "#e1e0d9";
const FUENTE = 'system-ui, -apple-system, "Segoe UI", sans-serif';

export const SERIES_TITULARES = ["sub:ingresos", "sub:costos", "sub:gastos", "ut:neta"];

const TEMA_NIVO = {
  fontFamily: FUENTE,
  fontSize: 11,
  textColor: TINTA_SUAVE,
  axis: {
    ticks: { text: { fill: MUTED, fontSize: 10 }, line: { stroke: REJILLA } },
    domain: { line: { stroke: REJILLA } },
  },
  grid: { line: { stroke: REJILLA, strokeWidth: 1 } },
  legends: { text: { fill: TINTA_SUAVE, fontSize: 11 } },
};

/** Ejes con importes largos: $1.2M lee mejor que $1,234,567.89. */
const montoCompacto = (valor) => {
  const numero = Number(valor) || 0;
  const signo = numero < 0 ? "-" : "";
  const absoluto = Math.abs(numero);
  if (absoluto >= 1000000) return `${signo}$${(absoluto / 1000000).toFixed(1)}M`;
  if (absoluto >= 1000) return `${signo}$${Math.round(absoluto / 1000)}k`;
  return `${signo}$${Math.round(absoluto)}`;
};

const etiquetaDeRenglon = (renglon) => {
  if (renglon.tipo === "cuenta") {
    return `${cuentaMask(renglon.codigo || "")} ${renglon.descripcion || ""}`.trim();
  }
  // "TOTAL INGRESOS" -> "Ingresos": en una leyenda el prefijo solo estorba.
  const limpio = (renglon.descripcion || "").replace(/^TOTAL\s+/i, "");
  return limpio.charAt(0) + limpio.slice(1).toLowerCase();
};

/**
 * Reparte slots de color entre los renglones seleccionados **conservando** el de
 * los que ya lo tenian.
 *
 * El color sigue a la entidad, nunca a su posicion: si se asignara por indice de
 * la seleccion, quitar una serie repintaria a las demas y quien aprendio que
 * "Sueldos es azul" quedaria enganado. Los que siguen seleccionados se quedan con
 * su slot y los nuevos toman el primer hueco libre.
 *
 * @param {string[]} seleccion   ids de renglon seleccionados
 * @param {Object} previo        el mapa devuelto por la llamada anterior
 * @returns {Object} id -> indice de slot en PALETA_SERIES
 */
export const asignarSlotsDeColor = (seleccion, previo = {}) => {
  const asignados = {};
  const ocupados = new Set();

  seleccion.forEach((id) => {
    if (previo[id] === undefined) return;
    asignados[id] = previo[id];
    ocupados.add(previo[id]);
  });

  seleccion.forEach((id) => {
    if (asignados[id] !== undefined) return;
    for (let slot = 0; slot < MAX_SERIES; slot++) {
      if (ocupados.has(slot)) continue;
      asignados[id] = slot;
      ocupados.add(slot);
      return;
    }
    // Sin slots libres: la paleta NO se cicla. El renglon se queda fuera de la
    // grafica y la UI lo dice.
  });

  return asignados;
};

/**
 * Series de la grafica. Con `seleccion` vacia usa los titulares del P&L; con
 * renglones seleccionados, esos. Devuelve siempre `[{id, color, data:[{x,y}]}]`,
 * la forma que consumen nivo, el PNG y cualquier otro destino futuro.
 */
export const seriesDeGrafica = (datos, seleccion, slots) => {
  if (!datos || !datos.periodos || !datos.renglones) return [];
  const { periodos, renglones } = datos;
  const porId = new Map(renglones.map((renglon) => [renglon.id, renglon]));
  const hayEleccion = seleccion && seleccion.length > 0;
  const ids = hayEleccion ? seleccion : SERIES_TITULARES;

  const usadas = new Set();
  return ids
    .map((id) => ({ id, renglon: porId.get(id) }))
    .filter((entrada) => entrada.renglon)
    .map((entrada, indice) => {
      // Los titulares van en orden fijo; los seleccionados, por slot estable.
      const slot = hayEleccion && slots ? slots[entrada.id] : indice;
      return { ...entrada, slot };
    })
    .filter((entrada) => entrada.slot !== undefined && entrada.slot < MAX_SERIES)
    .map(({ renglon, slot }) => {
      // nivo indexa por `id`: dos cuentas con la misma descripcion se pisarian.
      let etiqueta = etiquetaDeRenglon(renglon);
      while (usadas.has(etiqueta)) etiqueta = `${etiqueta} `;
      usadas.add(etiqueta);
      return {
        id: etiqueta,
        color: PALETA_SERIES[slot],
        data: periodos.map((periodo) => ({
          x: periodo.label,
          y: (renglon.importes || {})[periodo.key] || 0,
        })),
      };
    });
};

/**
 * El SVG que nivo ya dibujo, convertido a PNG para el PDF y el Excel.
 *
 * Devuelve `null` si no se puede (la grafica no esta montada, el navegador no
 * deja exportar el canvas): el llamador entonces exporta sin grafica, que es un
 * respaldo valido — mejor un reporte sin imagen que ningun reporte.
 */
export const rasterizarGrafica = (contenedor, opciones = {}) =>
  new Promise((resolve) => {
    try {
      const svg = contenedor && contenedor.querySelector("svg");
      if (!svg) return resolve(null);

      const ancho = Math.round(svg.getBoundingClientRect().width) || 900;
      const alto = Math.round(svg.getBoundingClientRect().height) || 320;
      const escala = opciones.escala || 2;

      const clon = svg.cloneNode(true);
      clon.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clon.setAttribute("width", ancho);
      clon.setAttribute("height", alto);

      // Al rasterizar, el SVG se dibuja aislado: no hereda el CSS de la pagina,
      // asi que la fuente hay que declararla dentro o el texto sale en serif.
      const estilo = document.createElementNS("http://www.w3.org/2000/svg", "style");
      estilo.textContent = `text { font-family: ${FUENTE}; }`;
      clon.insertBefore(estilo, clon.firstChild);

      const cadena = new XMLSerializer().serializeToString(clon);
      const url =
        "data:image/svg+xml;base64," +
        window.btoa(unescape(encodeURIComponent(cadena)));

      const imagen = new Image();
      imagen.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = ancho * escala;
          canvas.height = alto * escala;
          const ctx = canvas.getContext("2d");
          // Fondo explicito: un PNG transparente sobre el blanco del PDF se ve
          // bien, pero sobre el gris de Excel no.
          ctx.fillStyle = opciones.fondo || "#FFFFFF";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(imagen, 0, 0, canvas.width, canvas.height);
          resolve({ dataUrl: canvas.toDataURL("image/png"), ancho, alto });
        } catch (error) {
          resolve(null);
        }
      };
      imagen.onerror = () => resolve(null);
      imagen.src = url;
    } catch (error) {
      resolve(null);
    }
  });

const GraficaEstadoDeResultados = ({ series, periodos }) => {
  if (series.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0", color: MUTED, fontSize: 13 }}>
        No hay series que graficar con los filtros actuales.
      </div>
    );
  }

  const colores = series.map((serie) => serie.color);
  // La leyenda va siempre: con dos o mas series la identidad no puede depender
  // solo del color. La de barras ademas exige `dataFrom`, la de lineas no lo usa.
  const leyendaBase = {
    anchor: "top-left",
    direction: "row",
    translateY: -28,
    itemWidth: 150,
    itemHeight: 16,
    symbolSize: 10,
    symbolShape: "circle",
  };

  if (periodos.length <= MAX_PERIODOS_BARRAS) {
    const claves = series.map((serie) => serie.id);
    const datos = periodos.map((periodo, indice) => {
      const fila = { periodo: periodo.label };
      series.forEach((serie) => {
        fila[serie.id] = serie.data[indice] ? serie.data[indice].y : 0;
      });
      return fila;
    });

    return (
      <ResponsiveBar
        data={datos}
        keys={claves}
        indexBy="periodo"
        groupMode="grouped"
        margin={{ top: 40, right: 20, bottom: 50, left: 70 }}
        padding={0.25}
        innerPadding={2}
        borderRadius={3}
        colors={colores}
        theme={TEMA_NIVO}
        enableLabel={false}
        enableGridX={false}
        axisLeft={{ tickSize: 4, tickPadding: 6, format: montoCompacto }}
        axisBottom={{ tickSize: 4, tickPadding: 6 }}
        axisTop={null}
        axisRight={null}
        valueFormat={(valor) => dineroDisabledMask(valor)}
        legends={[{ ...leyendaBase, dataFrom: "keys" }]}
        animate={false}
      />
    );
  }

  // Con muchos periodos las etiquetas del eje se encivan: se muestra una de cada
  // n, calculada para no pasar de ~16 marcas.
  const paso = Math.ceil(periodos.length / 16);
  const marcas = periodos.map((p) => p.label).filter((_, i) => i % paso === 0);

  return (
    <ResponsiveLine
      data={series}
      colors={colores}
      theme={TEMA_NIVO}
      margin={{ top: 40, right: 20, bottom: 70, left: 70 }}
      xScale={{ type: "point" }}
      yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
      curve="monotoneX"
      lineWidth={2}
      pointSize={8}
      pointBorderWidth={2}
      // Anillo del color de la superficie sobre la marca, para que dos puntos
      // encimados se separen sin dibujarles un borde de contorno.
      pointColor={{ from: "serieColor" }}
      pointBorderColor="#FFFFFF"
      enableGridX={false}
      enableSlices="x"
      useMesh
      yFormat={(valor) => dineroDisabledMask(valor)}
      axisLeft={{ tickSize: 4, tickPadding: 6, format: montoCompacto }}
      axisBottom={{ tickSize: 4, tickPadding: 6, tickRotation: 45, tickValues: marcas }}
      axisTop={null}
      axisRight={null}
      legends={[leyendaBase]}
      animate={false}
    />
  );
};

export default GraficaEstadoDeResultados;
